/**
 * Timeout wrapper for fetch — prevents Supabase requests from hanging
 * forever during outages (e.g., 522 on auth endpoints).
 */

const TIMEOUT_MARKER = 'Supabase request timed out after';

export class SupabaseTimeoutError extends Error {
  public readonly isTimeout = true;

  constructor(url: string, timeoutMs: number) {
    super(`${TIMEOUT_MARKER} ${timeoutMs}ms: ${url}`);
    this.name = 'SupabaseTimeoutError';
  }
}

/**
 * Detect timeout errors even when the Supabase SDK wraps them
 * (e.g., auth wraps into `AuthRetryableFetchError`).
 */
export function isSupabaseTimeout(error: unknown): boolean {
  if (error instanceof SupabaseTimeoutError) return true;
  if (error && typeof error === 'object') {
    const e = error as Record<string, unknown>;
    if (typeof e.message === 'string' && e.message.includes(TIMEOUT_MARKER)) return true;
    if (e.cause && isSupabaseTimeout(e.cause)) return true;
  }
  return false;
}

// Edge function invocations can take 30-90s (RapidAPI calls, AI generation, etc.)
// Use a much longer timeout for those so we don't abort legitimate long-running calls.
const EDGE_FUNCTION_TIMEOUT_MS = 90_000;

export function createFetchWithTimeout(timeoutMs: number): typeof fetch {
  return (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;

    // Edge function calls need a longer timeout than auth/DB calls
    const effectiveTimeout = url.includes('/functions/v1/') ? EDGE_FUNCTION_TIMEOUT_MS : timeoutMs;

    const controller = new AbortController();

    // If the caller already provided a signal, listen for its abort too
    if (init?.signal) {
      init.signal.addEventListener('abort', () => controller.abort(init.signal!.reason));
    }

    const timer = setTimeout(() => controller.abort('timeout'), effectiveTimeout);

    return fetch(input, { ...init, signal: controller.signal })
      .catch((err) => {
        if (err?.name === 'AbortError' || controller.signal.aborted) {
          throw new SupabaseTimeoutError(url, effectiveTimeout);
        }
        throw err;
      })
      .finally(() => clearTimeout(timer));
  };
}
